import { useContext, useEffect, useState } from 'react';
import * as R from 'ramda';
import { startOfDay } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { AuthContext } from '../authentication/AuthContext';

/** makeFilterParser
 * Creates a function that returns a 'parsed" object based on the parsers provided for the corresponding keys
 * @param  {Object}   parsers   Object of parsers for each key in the filter object
 * @param  {Object}   obj       Object to be parsed
 * @returns Object
 *
 * This function can be used to create a parser function that can be used to parse the filters
 * based on the parsers provided by the same key in the object
 *
 * Parsers can also parse nested objects!
 *
    Example:

    import usePersistentFilters, { makeFilterParser } from 'hooks/usePersistentFilter';

    const myCustomParser = makeFilterParser(
      {
        endDate: dateTimeParser,
        startDate: dateTimeParser,
      },
      NestedProp: {
        comments: function addSmilieys(str) { return str + ' :)'; },
      }
    );

    const [filters, setFilters] = usePersistentFilters(
      'inventoryReports',
      {
        allocationGroup: null,
        endDate: null,
        productGroup: null,
        startDate: null,
        NestedProp: {
          comments: null,
        },
      }
      myCustomParser
    );
 */
export const makeFilterParser = parsers => obj => {
  return R.mergeDeepWith(
    (val, parser) => {
      if (typeof parser === 'function' && !R.isNil(val)) {
        return parser(val);
      }

      return val;
    },
    obj,
    parsers
  );
};

/** filtersCounter
 * @param  {Object}   initialState   Initial state of filters
 * @param  {Object}   filters        Filters object
 * @param  {Array}    ignorekeys     Array of keys to ignore
 * @param  {Function} parser         Parser function
 * @returns Number
 *
 * This function provides functionality to provide the number of filters applied against
 * the intial state of the filters
 * It also provides functionality to ignore keys if some are not relevant to the UI
 *

  Example:

  import { filtersCounter } from 'hooks/usePersistentFilter';

  props:
    initialState: Object
    filters: Object
    ignorekeys: Array<string> : An array of keys for the counter to ignore (optional)
    parser: (optional)
  ...
  const parser = makeFilterParser({
    endDate: dateParser,
    startDate: dateParser,
  });
  <Badge badgeContent={filtersCounter(initialState, filters, ignoreKeys, parser)} />

 */
export const filtersCounter = (initialState, filters, ignorekeys, parser) => {
  let parse = parser;
  const ignore = ignorekeys ?? [];
  if (typeof parse !== 'function') {
    parse = makeFilterParser(parse);
  }
  const parsed = parse(filters);
  const parsedInit = parse(initialState);
  const diff = (a, b) => {
    if (R.isNil(a) && R.isEmpty(b)) return false;
    if (R.isNil(b) && R.isEmpty(a)) return false;

    return a !== b;
  };

  return Object.keys(parsedInit)
    .filter(k => !ignore.includes(k))
    .reduce((a, c) => {
      const i = parsedInit[c];
      const f = parsed[c];
      if (diff(i, f)) {
        return a + 1;
      }
      return a;
    }, 0);
};

/** isDateString util function
 * Provides a function to check if a string is a valid date string
 * @param  {String}   date   Date string 'YYYY-MM-DDTHH:MM:SS.sZ'
 * @returns Boolean
 */
const isDateString = date => {
  const dateString = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{1,4}Z/;
  return dateString.test(date);
};

/** dateTimeParser util function
 * Provides a parser for converting MUI DateTimePicker Date object values to timestamps
 * @param  {String}   str   Date string 'YYYY-MM-DDTHH:MM:SS.sZ' or Date object or timestamp
 *
 * @returns number
 */
export const dateTimeParser = str => {
  if (!str) {
    return null;
  }
  if (typeof str === 'string') {
    if (isDateString(str)) {
      return Date.parse(new Date(str));
    }
    return str;
  }
  return Date.parse(new Date(str));
};

/** dateParser util function
 * Provides a parser for converting MUI DatePicker Date object values to timestamps
 * @param  {String}   str   Date string 'YYYY-MM-DDTHH:MM:SS.sZ' or Date object or timestamp
 *
 * @returns Date
 */
export const dateParser = str => {
  if (!str) {
    return null;
  }
  if (typeof str === 'string') {
    if (isDateString(str)) {
      return Date.parse(startOfDay(new Date(str)));
    }
    return str;
  }
  return Date.parse(startOfDay(new Date(str)));
};

/** endOfDayParser util function
 * Converts a date string or Date object to a timestamp representing the end of the day in local time
 * @param  {String|Date}   str   Date string 'YYYY-MM-DDTHH:MM:SS.sZ' or Date object or timestamp
 * @returns number
 */

export const CENTRAL_TIME_ZONE = 'America/Chicago';

export const BOUNDARY_TIME_BY_TYPE = {
  end: '23:59:59',
  start: '00:00:00',
};

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}/;

const getDatePart = (rawValue, parsedDate) => {
  if (typeof rawValue === 'string' && DATE_REGEX.test(rawValue)) {
    return rawValue.slice(0, 10);
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const toCentralDateParam = (value, boundaryType = 'start') => {
  if (!value) {
    return null;
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  const datePart = getDatePart(value, parsedDate);
  const boundaryTime = BOUNDARY_TIME_BY_TYPE[boundaryType] ?? BOUNDARY_TIME_BY_TYPE.start;
  const centralBoundaryUtcDate = zonedTimeToUtc(`${datePart}T${boundaryTime}`, CENTRAL_TIME_ZONE);

  return formatInTimeZone(centralBoundaryUtcDate, CENTRAL_TIME_ZONE, "yyyy-MM-dd'T'HH:mm:ssXXX");
};

/** usePersistentFilters
 * Persist and retrieve filter object values
 * @param  {String}   id        Unique id of the compoment to be saved and retreived by
 * @param  {Object}   init      Initial state of filters
 * @param  {Object}   parsers   Each id in init can be given a function od the same id to parse the value to the filter
 *
 * @returns Array [getter, setter, deferTo]
 *
 * This hook can be used in place of useState with the addition of id, initital state and special parsers for each field
 * Example Usage for Automatic Persistence:
 *
  import usePersistentFilters, { dateParser, makeFilterParser } from 'hooks/usePersistentFilter';

  const parser = makeFilterParser({
    endDate: dateParser,
    startDate: dateParser,
  });
  const [filters, setFilters] = usePersistentFilters(
    'inventoryReports', // the identifier id - this is saved in local storage as filters.inventoryReports
    { // the default state of the set of filters we are managing.
      allocationGroup: null,
      endDate: null,
      productGroup: null,
      reportType: null,
      startDate: null,
    },
    parser
  );
 *
 * Defering Persistence with the Optional deferTo Callback
 *
 * Sometimes, we don't want to automatically persist a set of fields to localStorage and prefer
 * to let the user click a button (or wait for some other event to occur) before we persist.
 * deferTo let's us do that while still operating like the ususal useState hook.
 *
 * Example Usage for Deffered Persistance:
  import usePersistentFilters, { dateParser, makeFilterParser } from 'hooks/usePersistentFilter';

  const parser = makeFilterParser({
    endDate: dateParser,
    startDate: dateParser,
  });
  const [filters, setFilters, deferTo] = usePersistentFilters(
    'reconsignments', // the identifier id - this is saved in local storage as filters.inventoryReports
    {
      ...initialState,
    },
    parser
  );

  <>
    ... filter fields
    <Button onClick={ deferTo(() => {
      ... your code run the search query
    } ) }>Search</Button>
  </>
 */
const usePersistentFilters = (id, init, parser) => {
  let parse = parser ?? {};
  let saveFunction = null;
  if (typeof parse !== 'function' && !R.isNil(parse)) {
    parse = makeFilterParser(parse);
  }
  const { userProfile } = useContext(AuthContext);
  const [userSub] = useState(userProfile?.sub ?? 'anonymous');
  const [initialState] = useState(parse(init));

  const isValid = filters => {
    if (R.isNil(filters) || R.isEmpty(filters)) {
      return false;
    }

    const keys = Object.keys(filters);
    const initialKeys = Object.keys(initialState);
    if (keys.length !== initialKeys.length) {
      return false;
    }

    for (let i = 0; i < keys.length; i += 1) {
      if (!initialKeys.includes(keys[i])) {
        return false;
      }
    }

    return true;
  };

  const getPersistentFilters = () => {
    const localStorageValue = localStorage.getItem(`${userSub}.filters.${id}`) ?? null;
    const filters = JSON.parse(localStorageValue);
    if (isValid(filters)) {
      return parse(filters);
    }

    return initialState;
  };

  const setPersistentFilters = filters => {
    const parsed = parse(filters);

    localStorage.setItem(`${userSub}.filters.${id}`, JSON.stringify(parsed));
  };

  const [filters, setFilters] = useState(getPersistentFilters());

  // Allows to defer peristence while executing callback
  const deferTo = cb => {
    saveFunction =
      cb ??
      (() => {
        /* no callback */
      });
    return () => {
      setPersistentFilters(filters);
      saveFunction();
    };
  };

  useEffect(() => {
    if (typeof saveFunction !== 'function') {
      setPersistentFilters(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, id, userSub]);

  return [filters, setFilters, deferTo];
};

export default usePersistentFilters;
